import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Box, Typography, FormHelperText, Chip, IconButton, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { common, createLowlight } from 'lowlight';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatStrikethrough,
    FormatListBulleted,
    FormatListNumbered,
    Code,
    FormatQuote,
    Link as LinkIcon,
    Image as ImageIcon,
    FormatClear,
} from '@mui/icons-material';

// Initialize lowlight with common languages
const lowlight = createLowlight(common);

const StyledEditorWrapper = styled(Box)(({ theme, error }) => ({
    border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
        borderColor: error ? theme.palette.error.main : theme.palette.text.primary,
    },
    '& .toolbar': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.5, 1),
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        alignItems: 'center',
    },
    '& .ProseMirror': {
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        fontSize: '14px',
        fontFamily: theme.typography.fontFamily,
        padding: theme.spacing(2),
        outline: 'none',
        '&:focus': {
            outline: 'none',
        },
        '& p': {
            marginBottom: theme.spacing(1),
            '&:last-child': {
                marginBottom: 0,
            },
        },
        '& p.is-editor-empty:first-child::before': {
            color: theme.palette.text.secondary,
            content: 'attr(data-placeholder)',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
        },
        '& h1, & h2, & h3': {
            fontWeight: 600,
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1),
        },
        '& h1': { fontSize: '2em' },
        '& h2': { fontSize: '1.5em' },
        '& h3': { fontSize: '1.17em' },
        '& pre': {
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#2d2d2d',
            color: '#d4d4d4',
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(2),
            marginBottom: theme.spacing(1),
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '13px',
            '& code': {
                backgroundColor: 'transparent',
                color: 'inherit',
                padding: 0,
                fontSize: 'inherit',
            },
        },
        '& code': {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            padding: '2px 4px',
            borderRadius: '3px',
            fontSize: '13px',
            fontFamily: 'monospace',
        },
        '& blockquote': {
            borderLeft: `3px solid ${theme.palette.divider}`,
            paddingLeft: theme.spacing(2),
            marginLeft: 0,
            marginBottom: theme.spacing(1),
            color: theme.palette.text.secondary,
        },
        '& ul, & ol': {
            paddingLeft: theme.spacing(3),
            marginBottom: theme.spacing(1),
        },
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            '&:hover': {
                textDecoration: 'none',
            },
        },
        '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: theme.shape.borderRadius,
        },
    },
}));

const ToolbarButton = styled(IconButton)(({ theme, active }) => ({
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: active ? theme.palette.action.selected : 'transparent',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const ToolbarDivider = styled(Divider)({
    height: 24,
    margin: '0 4px',
});

const MenuBar = ({ editor, toolbar }) => {
    const setLink = useCallback(() => {
        if (!editor) return;
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        if (url === null) {
            return;
        }

        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    const addImage = useCallback(() => {
        if (!editor) return;
        const url = window.prompt('Image URL');

        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    }, [editor]);

    if (!editor || toolbar === false) {
        return null;
    }

    const buttons = {
        bold: {
            icon: FormatBold,
            action: () => editor.chain().focus().toggleBold().run(),
            isActive: () => editor.isActive('bold'),
            title: 'Bold',
        },
        italic: {
            icon: FormatItalic,
            action: () => editor.chain().focus().toggleItalic().run(),
            isActive: () => editor.isActive('italic'),
            title: 'Italic',
        },
        underline: {
            icon: FormatUnderlined,
            action: () => editor.chain().focus().toggleUnderline().run(),
            isActive: () => editor.isActive('underline'),
            title: 'Underline',
        },
        strike: {
            icon: FormatStrikethrough,
            action: () => editor.chain().focus().toggleStrike().run(),
            isActive: () => editor.isActive('strike'),
            title: 'Strikethrough',
        },
        bulletList: {
            icon: FormatListBulleted,
            action: () => editor.chain().focus().toggleBulletList().run(),
            isActive: () => editor.isActive('bulletList'),
            title: 'Bullet List',
        },
        orderedList: {
            icon: FormatListNumbered,
            action: () => editor.chain().focus().toggleOrderedList().run(),
            isActive: () => editor.isActive('orderedList'),
            title: 'Numbered List',
        },
        codeBlock: {
            icon: Code,
            action: () => editor.chain().focus().toggleCodeBlock().run(),
            isActive: () => editor.isActive('codeBlock'),
            title: 'Code Block',
        },
        blockquote: {
            icon: FormatQuote,
            action: () => editor.chain().focus().toggleBlockquote().run(),
            isActive: () => editor.isActive('blockquote'),
            title: 'Quote',
        },
        link: {
            icon: LinkIcon,
            action: setLink,
            isActive: () => editor.isActive('link'),
            title: 'Link',
        },
        image: {
            icon: ImageIcon,
            action: addImage,
            isActive: () => false,
            title: 'Image',
        },
        clear: {
            icon: FormatClear,
            action: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
            isActive: () => false,
            title: 'Clear Formatting',
        },
    };

    const toolbarPresets = {
        full: ['bold', 'italic', 'underline', 'strike', 'divider', 'bulletList', 'orderedList', 'divider', 'blockquote', 'codeBlock', 'divider', 'link', 'image', 'divider', 'clear'],
        basic: ['bold', 'italic', 'underline', 'divider', 'bulletList', 'orderedList', 'divider', 'codeBlock', 'clear'],
        minimal: ['bold', 'italic', 'underline', 'divider', 'bulletList', 'divider', 'codeBlock', 'clear'],
    };

    const activeButtons = typeof toolbar === 'string' ? toolbarPresets[toolbar] : ['bold', 'italic', 'underline'];

    return (
        <Box className="toolbar">
            {activeButtons.map((buttonName, index) => {
                if (buttonName === 'divider') {
                    return <ToolbarDivider key={`divider-${index}`} orientation="vertical" />;
                }

                const button = buttons[buttonName];
                if (!button) return null;

                const Icon = button.icon;
                return (
                    <ToolbarButton
                        key={buttonName}
                        onClick={button.action}
                        active={button.isActive()}
                        title={button.title}
                        size="small"
                    >
                        <Icon fontSize="small" />
                    </ToolbarButton>
                );
            })}
        </Box>
    );
};

const RichTextEditor = ({
    value = '',
    onChange,
    placeholder = 'Start typing...',
    label,
    error,
    helperText,
    readOnly = false,
    minHeight = '200px',
    maxHeight,
    toolbar = 'full',
    enableCodeHighlight = true,
    enableAutoSave = false,
    autoSaveKey = null,
    autoSaveInterval = 30000,
    onAutoSave,
    showLastSaved = false,
}) => {
    const [lastSaved, setLastSaved] = React.useState(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                codeBlock: enableCodeHighlight ? false : undefined,
            }),
            Link.configure({
                openOnClick: false,
            }),
            Image,
            ...(enableCodeHighlight ? [CodeBlockLowlight.configure({ lowlight })] : []),
        ],
        content: value,
        editable: !readOnly,
        editorProps: {
            attributes: {
                'data-placeholder': placeholder,
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            if (onChange) {
                onChange(html);
            }
        },
    });

    // Update editor content when value changes externally
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value, false);
        }
    }, [value, editor]);

    // Update editable state
    useEffect(() => {
        if (editor) {
            editor.setEditable(!readOnly);
        }
    }, [readOnly, editor]);

    // Auto-save functionality
    const saveDraft = useCallback(() => {
        if (autoSaveKey && value) {
            try {
                localStorage.setItem(autoSaveKey, value);
                setLastSaved(new Date());
                if (onAutoSave) {
                    onAutoSave(value);
                }
            } catch (error) {
                console.error('Failed to save draft:', error);
            }
        }
    }, [value, autoSaveKey, onAutoSave]);

    // Load draft on mount
    useEffect(() => {
        if (enableAutoSave && autoSaveKey && !value) {
            try {
                const saved = localStorage.getItem(autoSaveKey);
                if (saved && onChange) {
                    onChange(saved);
                    setLastSaved(new Date());
                }
            } catch (error) {
                console.error('Failed to load draft:', error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-save interval
    useEffect(() => {
        if (enableAutoSave && autoSaveKey) {
            const timer = setInterval(saveDraft, autoSaveInterval);
            return () => clearInterval(timer);
        }
    }, [enableAutoSave, autoSaveKey, autoSaveInterval, saveDraft]);

    const editorStyle = {
        minHeight,
        maxHeight,
        overflow: maxHeight ? 'auto' : undefined,
    };

    return (
        <Box>
            {(label || (showLastSaved && lastSaved)) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    {label && (
                        <Typography
                            variant="subtitle2"
                            sx={{ color: error ? 'error.main' : 'text.primary' }}
                        >
                            {label}
                        </Typography>
                    )}
                    {showLastSaved && lastSaved && (
                        <Chip
                            label={`Saved: ${lastSaved.toLocaleTimeString()}`}
                            size="small"
                            variant="outlined"
                            color="success"
                        />
                    )}
                </Box>
            )}
            <StyledEditorWrapper error={error}>
                <MenuBar editor={editor} toolbar={toolbar} />
                <Box style={editorStyle}>
                    <EditorContent editor={editor} />
                </Box>
            </StyledEditorWrapper>
            {helperText && (
                <FormHelperText error={error} sx={{ mt: 0.5, mx: 1.75 }}>
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

export default RichTextEditor;
